import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRequestQueryContext } from 'src/features/requests/use-request-query-context';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useCompanyQueryContextData } from 'src/features/companies/use-company-query-context';
import { useProfile } from 'src/models';
import { useRequestStatusMap } from 'src/pages/RequestPage/RequestStatusChangeForm/RequestStatusChangeForm.utils';
import { useOpenRequestStatusChangeModal } from 'src/features/requests/request-status-change/use-open-request-status-change-modal';
import { Button } from 'src/components/ui/button';
import { If } from 'src/components/If';
import { Link } from 'src/components/ui/link';
import { appRoutes } from 'src/routes';
import { RequestAttentionAction } from 'src/pages/RequestPage/RequestActionComponents/RequestAttentionAction';
import { authQueries } from 'src/api/queries';
import { ChevronLeft, Download, FileVideo2, FolderInput, Megaphone, Video } from 'lucide-react';
import { useToast } from 'src/components/ui/use-toast';
import { 
  downloadFilesAsZip, 
  getFilenameFromUrl, 
  ensureUniqueFilenames,
  type DownloadableFile,
  type DownloadProgress 
} from 'src/lib/utils/download-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { PageHeading, PageHeadingTitle } from 'src/components/ui/page-heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/components/ui/breadcrumb';
import { defaultSortFilter } from 'src/api/services/RequestClient';
import { requestsResponseHelper } from 'src/features/requests/requests-response.helper';
import { DefaultToMultiOutcomeRequestTransformButton } from 'src/features/requests/request-table/default-to-multi-outcome-request-transform-button';
import { Loader2 } from 'src/components/ui/animated-loader2';

const RequestDefaultPageContentTop = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const client = useQueryClient();

  const requestQuery = useRequestQueryContext();
  const request = useDecoratedRequestContext();
  const company = useCompanyQueryContextData();

  const user = useProfile({ enabled: false, retry: false });

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const { toast } = useToast();

  const { canChangeStatus } = useRequestStatusMap();

  const { openRequestStatusChangeModal } = useOpenRequestStatusChangeModal();

  const handleDownloadAll = async () => {
    if (!request.id) return;
    
    setIsDownloading(true);
    setDownloadProgress(null);
    
    try {
      // Collect downloadable files from request media and assets
      const files: DownloadableFile[] = [];
      
      // Add media files
      request.media?.forEach((media) => {
        if (media.index_url) {
          files.push({
            url: media.index_url,
            filename: media.file_name || getFilenameFromUrl(media.index_url),
            mimeType: media.mime_type || undefined
          });
        }
      });

      // Add asset URLs (if they are direct file links)
      request.assets?.forEach((asset, index) => {
        // Check if asset URL looks like a direct file link
        if (asset.url && (asset.url.includes('.') || asset.url.includes('download'))) {
          files.push({
            url: asset.url,
            filename: getFilenameFromUrl(asset.url, `asset-${index + 1}`),
          });
        }
      });

      if (files.length === 0) {
        toast({
          variant: 'destructive',
          description: t('actions:no_files_description', { 
            defaultValue: 'No downloadable files found in this request.' 
          }),
        });
        return;
      }

      // Ensure unique filenames
      const uniqueFiles = ensureUniqueFilenames(files);
      
      // Use client-side bundling with progress tracking
      await downloadFilesAsZip(
        uniqueFiles, 
        `${request.title || 'project'}-files.zip`,
        (progress: DownloadProgress) => {
          setDownloadProgress(progress);
        }
      );
      
      toast({
        variant: 'success',
        description: t('actions:client_download_success', { 
          defaultValue: `Successfully downloaded ${files.length} files.` 
        }),
      });

    } catch (error) {
      console.error('Download failed:', error);
      toast({
        variant: 'destructive',
        description: t('actions:download_error_description', { 
          defaultValue: 'Failed to download project files. Please try again.' 
        }),
      });
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  const changeStatusButtonProps: React.ComponentProps<typeof Button> = {
    onClick: () =>
      openRequestStatusChangeModal({ requestId: request.id, companyId: company?.id ?? '' }),
    className: 'tw-order-first md:tw-order-last tw-grow sm:tw-grow-0',
    size: 'sm',
  };

  if (user.isAdmin && !request.hasEditor) {
    changeStatusButtonProps['title'] = t(
      "Status is not editable on requests that doesn't have assigned editor.",
    )!;
    changeStatusButtonProps['disabled'] = true;
  }

  if (!canChangeStatus(request?.status)) {
    changeStatusButtonProps['title'] = t("Status can't be changed for completed requests.")!;
    changeStatusButtonProps['disabled'] = true;
  }

  return (
    <div ref={ref} className={className} {...props}>
      <If
        when={!!children}
        else={
          <div className={'tw-mb-4 tw-flex tw-flex-wrap tw-gap-4 md:tw-mb-10'}>
            <div
              className={
                'tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 md:tw-justify-start'
              }
            >
              <PageHeading>
                <Breadcrumb>
                  <BreadcrumbList className={'tw-flex-nowrap'}>
                    <BreadcrumbItem>
                      <Button
                        variant={'unset'}
                        size={'iconXs'}
                        onClick={() => {
                          if (location.state && location.state.from) {
                            navigate(location.state.from);
                          } else {
                            navigate({
                              pathname: generatePath(appRoutes.companyRequestsList, {
                                companyId: company?.id ?? '',
                              }),
                              search: defaultSortFilter,
                            });
                          }
                        }}
                      >
                        <ChevronLeft className={'tw-size-4'} />
                      </Button>
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={appRoutes.companiesList}>{t('Companies')}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link
                          to={generatePath(appRoutes.company, { companyId: company?.id ?? '' })}
                        >
                          {company?.name ?? t('unset')!}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link
                          to={{
                            pathname: generatePath(appRoutes.companyRequestsList, {
                              companyId: company?.id ?? '',
                            }),
                            search: defaultSortFilter,
                          }}
                          state={location.state}
                        >
                          {t('Requests')}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbPage>
                      {t('common:revisions', {
                        defaultValue: 'Revisions',
                      })}
                    </BreadcrumbPage>
                  </BreadcrumbList>
                </Breadcrumb>
                <PageHeadingTitle className={'tw-flex-nowrap'}>
                  <div>
                    <Video className={'tw-size-6'} />
                  </div>
                  <span className={'tw-line-clamp-1'}>
                    {request?.title ?? t('unset', { ns: 'common' })!}
                  </span>
                </PageHeadingTitle>
              </PageHeading>
            </div>
            <div
              className={'tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2'}
            >
              <If when={request.canRequestAttention}>
                <RequestAttentionAction
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  form={{
                    requestId: request.id,
                    initialValues: {
                      attention_text: request?.attention_text || '',
                    },
                    onSubmit: async ({ attention_text }) => {
                      requestQuery.setQueryData({
                        attention_text,
                        requested_attention_at: new Date().toDateString(),
                      });
                      client.invalidateQueries({
                        queryKey: [authQueries.requests, request?.status],
                      });
                    },
                  }}
                  as={Button}
                  variant={'outline'}
                  size={'iconStartSm'}
                  className={'tw-grow sm:tw-grow-0'}
                >
                  <Megaphone className={'tw-size-5'} />
                  {t('Request attention')}
                </RequestAttentionAction>
              </If>

              <If when={requestsResponseHelper.canBeTransformedToMultiOutcome(request)}>
                <DefaultToMultiOutcomeRequestTransformButton
                  size={'iconStartSm'}
                  variant={'outline'}
                  className={'tw-grow sm:tw-grow-0'}
                  request={request}
                >
                  {(props) => (
                    <>
                      {props.isLoading ? (
                        <Loader2 size={16} variant={'unset'} />
                      ) : (
                        <FolderInput size={16} />
                      )}
                      {t('Transform')}
                    </>
                  )}
                </DefaultToMultiOutcomeRequestTransformButton>
              </If>

              {request.hasMedia && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleDownloadAll}
                        disabled={isDownloading}
                        variant="outline"
                        size="iconStartSm"
                        className="tw-grow sm:tw-grow-0 tw-gap-2"
                      >
                        {isDownloading ? (
                          <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
                        ) : (
                          <Download className="tw-h-4 tw-w-4" />
                        )}
                        {downloadProgress ? (
                          <span className="tw-text-xs">
                            {downloadProgress.current}/{downloadProgress.total}
                          </span>
                        ) : (
                          t('actions:download_all', { defaultValue: 'Download All' })
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {downloadProgress
                        ? `${t('actions:downloading', { defaultValue: 'Downloading' })}: ${downloadProgress.filename || ''}`
                        : t('actions:download_all_tooltip', {
                            defaultValue: 'Download all project files as a zip archive',
                          })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <If when={request.hasRevisions}>
                <Button
                  asChild
                  variant={'outline'}
                  size={'iconStartSm'}
                  className={'tw-grow sm:tw-grow-0'}
                >
                  <Link
                    variant={'unset'}
                    to={generatePath(appRoutes.requestRevisions, {
                      companyId: company?.id ?? '',
                      requestId: request.id,
                    })}
                  >
                    <FileVideo2 className={'tw-size-5'} />
                    {t('Revisions')}
                  </Link>
                </Button>
              </If>

              <Button {...changeStatusButtonProps}>{t('Change status')}</Button>
            </div>
          </div>
        }
      >
        {children}
      </If>
    </div>
  );
});
RequestDefaultPageContentTop.displayName = 'RequestDefaultPageContentTop';

export { RequestDefaultPageContentTop };
